import {
	Card,
	ControlledInput,
	ControlledRadioGroup,
	TextInputListController,
} from "@dune/ui";
import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMutateGetSocietyInfoFromSiren } from "../../../../../../api/queries/siren.queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";

export const BrokerDetails: React.FC = () => {
	const {
		control,
		setValue,
		setError,
		clearErrors,
		formState: { errors },
	} = useFormContext();
	const [sirenSearchStatus, setSirenSearchStatus] = useState<
		"idle" | "searching" | "found" | "error"
	>("idle");
	const [sirenErrorMessage, setSirenErrorMessage] = useState<string>("");

	const attachedToNetwork = useWatch({ control, name: "attachedToANetwork" });
	const attachedToGroup = useWatch({ control, name: "attachedToAGroup" });
	const sirenGroup = useWatch({ control, name: "sirenGroup" });

	const debouncedSirenGroup = useDebounce(sirenGroup, 1000);
	const { mutate: getSocietyInfo } = useMutateGetSocietyInfoFromSiren();

	useEffect(() => {
		if (debouncedSirenGroup && debouncedSirenGroup.length >= 8) {
			setSirenSearchStatus("searching");
			setSirenErrorMessage("");
			clearErrors("sirenGroup");

			getSocietyInfo(debouncedSirenGroup, {
				onSuccess: (data) => {
					if (data?.denomination) {
						setValue("companyGroupName", data.denomination);
						setValue("_isGroupSirenValid", "true");
						setSirenSearchStatus("found");
						clearErrors("sirenGroup");
					} else {
						setValue("companyGroupName", "");
						setValue("_isGroupSirenValid", "false");
						setSirenSearchStatus("error");
						setSirenErrorMessage("Aucune société trouvée pour ce SIREN");
						setError("sirenGroup", {
							type: "manual",
							message: "SIREN non trouvé",
						});
					}
				},
				onError: (_error) => {
					setValue("companyGroupName", "");
					setValue("_isGroupSirenValid", "false");
					setSirenSearchStatus("error");
					setSirenErrorMessage("Erreur lors de la recherche du SIREN");
					setError("sirenGroup", {
						type: "manual",
						message: "SIREN invalide",
					});
				},
			});
		} else if (
			debouncedSirenGroup &&
			debouncedSirenGroup.length > 0 &&
			debouncedSirenGroup.length < 8
		) {
			setValue("companyGroupName", "");
			setValue("_isGroupSirenValid", "false");
			setSirenSearchStatus("error");
			setSirenErrorMessage("Le SIREN doit contenir au moins 8 caractères");
			setError("sirenGroup", {
				type: "manual",
				message: "SIREN trop court",
			});
		} else {
			setValue("companyGroupName", "");
			setValue("_isGroupSirenValid", "true");
			setSirenSearchStatus("idle");
			setSirenErrorMessage("");
			clearErrors("sirenGroup");
		}
	}, [debouncedSirenGroup, getSocietyInfo, setValue, setError, clearErrors]);

	return (
		<div className="py-4">
			<div className="flex flex-row gap-4">
				<div className="flex flex-col gap-4 pb-3 w-full">
					<div className="text-text-main-blue  font-semibold">Réseau</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-col gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<ControlledRadioGroup
									control={control}
									name="attachedToANetwork"
									label="Rattaché à un réseau ?"
									isFilled
									items={[
										{ id: "network-true", value: "true", label: "Oui" },
										{ id: "network-false", value: "false", label: "Non" },
									]}
								/>
							</div>
							{attachedToNetwork === "true" && (
								<div className="flex flex-col items-start gap-3 w-full">
									<ControlledInput
										label="Nom du réseau"
										control={control}
										name="networkName"
									/>
								</div>
							)}
						</div>
					</Card>
					<div className="text-text-main-blue  font-semibold">
						Entités rattachées
					</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-col gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<TextInputListController name="entityList" control={control} />
							</div>
						</div>
					</Card>
				</div>
				<div className="flex flex-col gap-4 pb-3 w-full">
					<div className="text-text-main-blue  font-semibold">Groupe</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-col gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<ControlledRadioGroup
									control={control}
									name="attachedToAGroup"
									label="Rattaché à un groupe ?"
									isFilled
									items={[
										{ id: "group-true", value: "true", label: "Oui" },
										{ id: "group-false", value: "false", label: "Non" },
									]}
								/>
							</div>
							{attachedToGroup === "true" && (
								<>
									<div className="flex flex-col items-start gap-3 w-full">
										<ControlledInput
											label="SIREN"
											control={control}
											name="sirenGroup"
											error={errors.sirenGroup?.message as string}
										/>
										{sirenSearchStatus === "searching" && (
											<div className="text-sm text-blue-600">
												Recherche en cours...
											</div>
										)}
										{sirenSearchStatus === "error" && sirenErrorMessage && (
											<div className="text-sm text-red-600">
												{sirenErrorMessage}
											</div>
										)}
									</div>
									<div className="flex flex-col items-start gap-3 w-full">
										<ControlledInput
											label="Raison sociale"
											control={control}
											name="companyGroupName"
											disabled
											placeholder={
												sirenSearchStatus === "found"
													? ""
													: "Entrez un SIREN valide pour afficher la raison sociale"
											}
										/>
									</div>
								</>
							)}
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};
